import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import styles from './PillarBar.module.css';

/**
 * Five-pillar icon bar — Problems, Solutions, Projects (center L4E logo),
 * Enablers, Communities.
 *
 * DEV-1447 — the glyphs are now NAVIGABLE. This bar is the ONLY pillar
 * navigation surface on Discover: the globe is the hub, these are the spokes,
 * and a pillar catalog is a leaf that carries no pillar nav of its own (a rail
 * or tab strip inside a catalog would be a second copy of this bar — and would
 * also be Discover's third left column, which POLICY_NAVIGATION_RAILS §3
 * forbids). To switch catalogs the user returns here.
 *
 * Catalogs are ROUTES, not state — browser Back returns to the globe only if
 * the catalog owns a URL.
 *
 * Unbuilt pillars are DISABLED-AND-EXPLAINED, not hidden (CLAUDE.md). They are
 * pre-existing identity elements of the globe overlay; removing three of five
 * to honour "no dead tabs" would damage the surface the grammar exists to
 * protect. Hovering a locked glyph swaps its label for the earning path.
 * Progressive *enabling*, not progressive *appearance*.
 */

/** Route per pillar. `null` = catalog not built yet → disabled-and-explained. */
const PILLAR_ROUTES: Record<string, string | null> = {
  problems: null,
  solutions: null,
  projects: null,
  enablers: null,
  communities: '/discover/communities',
};

export default function PillarBar({ dissolving = false, drawerOpen = false }: { dissolving?: boolean; drawerOpen?: boolean }) {
  const { t } = useAppTranslation('discover');
  const navigate = useNavigate();

  /** Shared props for a pillar glyph: navigable, or locked with its reason. */
  const pillarProps = (key: keyof typeof PILLAR_ROUTES, pillarClass: string, exitClass: string) => {
    const route = PILLAR_ROUTES[key];
    const locked = !route;
    const label = t(`pillar.${key}`);
    return {
      type: 'button' as const,
      className: [
        styles.pillar,
        pillarClass,
        locked ? styles.pillarLocked : '',
        dissolving ? exitClass : '',
      ].filter(Boolean).join(' '),
      onClick: () => { if (route) navigate(route); },
      'aria-disabled': locked || undefined,
      title: locked ? t('catalog.comingSoon', { pillar: label }) : label,
    };
  };

  /** Label row — swaps to the earning path on hover when the pillar is locked. */
  const PillarLabel = ({ pillarKey }: { pillarKey: keyof typeof PILLAR_ROUTES }) => {
    const locked = !PILLAR_ROUTES[pillarKey];
    const label = t(`pillar.${pillarKey}`);
    return (
      <div className={styles.pillarLabel}>
        <span className={styles.pillarLabelText}>{label}</span>
        {locked && (
          <span className={styles.pillarLockedText}>{t('catalog.comingSoonShort')}</span>
        )}
      </div>
    );
  };

  return (
    <div className={`${styles.pillarsBar} ${dissolving ? styles.pillarsBarExit : ''} ${drawerOpen ? styles.pillarsBarShifted : ''}`}>
      {/* PROBLEMS — Red with warning triangle */}
      <button {...pillarProps('problems', styles.pillarProblems, styles.pillarExit2)}>
        <div className={styles.pillarIcon}>
          <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M7 33h26L20 7 7 33zm14.5-4.5h-3v-3h3v3zm0-6h-3v-6h3v6z"
              fill="rgba(0,0,0,0.35)"
              stroke="rgba(0,0,0,0.55)"
              strokeWidth="1"
            />
          </svg>
        </div>
        <PillarLabel pillarKey="problems" />
      </button>

      {/* SOLUTIONS — Green with puzzle piece */}
      <button {...pillarProps('solutions', styles.pillarSolutions, styles.pillarExit1)}>
        <div className={styles.pillarIcon}>
          <svg viewBox="0 0 24 24" width="55%" height="55%" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M20.5 11H19V7a2 2 0 0 0-2-2h-4V3.5a2.5 2.5 0 0 0-5 0V5H4a2 2 0 0 0-2 2v3.8h1.5a2.5 2.5 0 0 1 0 5H2V20a2 2 0 0 0 2 2h3.8v-1.5a2.5 2.5 0 0 1 5 0V22H17a2 2 0 0 0 2-2v-4h1.5a2.5 2.5 0 0 0 0-5z"
              fill="rgba(0,0,0,0.35)"
              stroke="rgba(0,0,0,0.55)"
              strokeWidth="0.8"
            />
          </svg>
        </div>
        <PillarLabel pillarKey="solutions" />
      </button>

      {/* PROJECTS — Center, spinning L4E wheel */}
      <button {...pillarProps('projects', styles.pillarProjects, styles.pillarExit0)}>
        <div className={styles.pillarIcon}>
          {/* Single SVG — shared coordinate space eliminates alignment issues */}
          <svg
            className={styles.projectsWheel}
            viewBox="-15 -15 158 143"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
          >
            {/* Spinning outer arcs — rotate around inner ring center (64, 64) */}
            <g className={styles.wheelOuter}>
              <path
                d="M107.574 43.0177C114.543 45.6668 120.513 41.244 121.466 35.3008C143.023 81.9018 107.62 125.992 68.0824 127.581C60.1377 125.508 59.8821 115.902 67.9663 112.171C96.0285 110.051 123.301 78.3774 107.574 43.0177Z"
                fill="#45BB47"
              />
              <path
                d="M10.0098 29.7194C17.397 28.0148 22.0431 30.756 24.0409 37.7128C42.416 10.9915 82.1631 7.09848 103.744 36.4919C111.364 43.2643 121.213 35.3171 116.242 26.863C89.3877 -10.6159 35.1914 -8.15106 10.0098 29.7194Z"
                fill="#106DD5"
              />
              <path
                d="M60.3411 112.197C55.9274 116.413 55.1608 123.185 60.3411 128C11.8595 122.586 -10.9062 77.4365 5.05298 37.7692C10.303 29.8679 20.8264 34.0835 19.6881 44.1039C8.35168 67.3929 19.8972 107.728 60.3411 112.197Z"
                fill="#D53541"
              />
            </g>
            {/* Static white inner ring */}
            <path
              d="M64.1983 23.4648C41.7114 23.4648 23.4756 41.5478 23.4756 63.8463C23.4756 86.1447 41.7114 104.228 64.2216 104.228C86.7085 104.228 104.944 86.1447 104.944 63.8463C104.944 41.5478 86.7085 23.4648 64.1983 23.4648ZM63.9428 88.3561C50.4227 88.3561 39.4813 77.5064 39.4813 64.0997C39.4813 50.6929 50.4227 39.8432 63.9428 39.8432C77.4628 39.8432 88.4043 50.6929 88.4043 64.0997C88.4043 77.5064 77.4628 88.3561 63.9428 88.3561Z"
              fill="white"
            />
          </svg>
        </div>
        <PillarLabel pillarKey="projects" />
      </button>

      {/* ENABLERS — Blue with people.
          NOTE (POLICY_ENABLER_VOCABULARY): the PILLAR stays "Enabler" — this
          glyph and its `enablers` key are correct and stay. Only the *entity*
          label changes elsewhere ("Organizations" in Engagements). Do not
          rename this. */}
      <button {...pillarProps('enablers', styles.pillarEnablers, styles.pillarExit1)}>
        <div className={styles.pillarIcon}>
          <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="12" r="4" fill="var(--text-primary)" />
            <path
              d="M13 28 C13 22 16 19 20 19 C24 19 27 22 27 28"
              fill="var(--text-tertiary)"
            />
            <circle cx="11" cy="15" r="3" fill="var(--text-tertiary)" />
            <path
              d="M5 28 C5 23 7 21 11 21 C13 21 14.5 22 15 23"
              fill="var(--text-muted)"
            />
            <circle cx="29" cy="15" r="3" fill="var(--text-tertiary)" />
            <path
              d="M35 28 C35 23 33 21 29 21 C27 21 25.5 22 25 23"
              fill="var(--text-muted)"
            />
          </svg>
        </div>
        <PillarLabel pillarKey="enablers" />
      </button>

      {/* COMMUNITIES — White with connected people. The one live catalog. */}
      <button {...pillarProps('communities', styles.pillarCommunities, styles.pillarExit2)}>
        <div className={styles.pillarIcon}>
          <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="13" r="3.5" fill="#555" />
            <path
              d="M14.5 27 C14.5 22 16.5 19.5 20 19.5 C23.5 19.5 25.5 22 25.5 27"
              fill="#555"
            />
            <line x1="12" y1="16" x2="17" y2="15" stroke="#777" strokeWidth="1" />
            <line x1="28" y1="16" x2="23" y2="15" stroke="#777" strokeWidth="1" />
            <line x1="14" y1="26" x2="10" y2="24" stroke="#777" strokeWidth="1" />
            <line x1="26" y1="26" x2="30" y2="24" stroke="#777" strokeWidth="1" />
            <circle cx="10" cy="14" r="2.5" fill="#666" />
            <circle cx="30" cy="14" r="2.5" fill="#666" />
            <circle cx="8" cy="25" r="2" fill="#777" />
            <circle cx="32" cy="25" r="2" fill="#777" />
          </svg>
        </div>
        <PillarLabel pillarKey="communities" />
      </button>
    </div>
  );
}
